// ts-rest contract for batch ISIN → security-identity resolution (PRD #210).
//
// A presentation projection of the firm-infrastructure `security_reference`
// cache — NOT a domain model. The controller is the mapping seam: inbound it
// takes the ISIN list; outbound it projects the repo rows (cached + freshly
// read-through-resolved) onto the shape below.
//
// One endpoint:
//   POST /api/security-reference/resolve
//     body:    { isins: string[] }
//     returns: { references: [{ isin, status, ticker?, name?, figi?,
//                               exchangeCode?, marketSector? }] }
//
// A miss or slow OpenFIGI never blocks the page — the resolver degrades a
// failed lookup to `rate-limited` / `error` status and the web layer renders a
// monogram. `not-found` / `error` rows carry no identity fields.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

/** Per-ISIN resolution outcome, mirroring the resolver / cache status. */
export const securityRefStatusSchema = z.enum(['resolved', 'not-found', 'rate-limited', 'error']);
export type SecurityRefStatusDto = z.infer<typeof securityRefStatusSchema>;

/**
 * One resolved (or unresolved) security identity. Identity fields are present
 * only when `status === 'resolved'`; every other status returns just the ISIN
 * and status so the caller degrades to a monogram.
 */
export const securityReferenceSchema = z.object({
    isin: z.string(),
    status: securityRefStatusSchema,
    ticker: z.string().optional(),
    name: z.string().optional(),
    figi: z.string().optional(),
    exchangeCode: z.string().optional(),
    marketSector: z.string().optional(),
});
export type SecurityReferenceDto = z.infer<typeof securityReferenceSchema>;

export const resolveSecurityReferenceRequestSchema = z.object({
    isins: z.array(z.string()),
});
export type ResolveSecurityReferenceRequest = z.infer<typeof resolveSecurityReferenceRequestSchema>;

export const resolveSecurityReferenceResponseSchema = z.object({
    references: z.array(securityReferenceSchema),
});
export type ResolveSecurityReferenceResponse = z.infer<
    typeof resolveSecurityReferenceResponseSchema
>;

export const securityReferenceContract = c.router(
    {
        resolve: {
            method: 'POST',
            path: '/security-reference/resolve',
            body: resolveSecurityReferenceRequestSchema,
            responses: {
                200: resolveSecurityReferenceResponseSchema,
            },
            summary:
                'Batch-resolve ISINs to cached security identities (read-through OpenFIGI cache).',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
